import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { generatePath, useNavigate } from 'react-router-dom';

import OutputReportActions from '../../actions/OutputReportActions';
import useIsLoggedIn from '../../selectors/useIsLoggedIn';
import useScrollToTop from '../../hooks/useScrollToTop';
import ProjectsPageLayout from '../../components/layout/ProjectsPageLayout/ProjectsPageLayout';
import ProjectsSection from '../../components/projects/ProjectsSection/ProjectsSection';
import OutputReportCard from '../../components/cards/OutputReportCard/OutputReportCard';
import { OUTPUT_REPORT_DETAIL_PATH } from '../../constants/route.constants';
import IReports from '../../types/IReports';
import NoData from '../../components/NoData/NoData';
import { Translate } from 'react-translate.ts';
import { AppDispatch } from '../../reducers/store';
import LanguageDialog from '../../components/dialog/LanguageDialog/LanguageDialog';
import { usePortal } from '../../components/PortalProvider';
import IReport from '../../types/IReport';
import OutputReportSelectors from '../../selectors/OutputReportSelectors';
import LanguageSlice from '../../common/slices/LanguageSlice';

export default function OutputReportsPage() {
    const { data: reports, loading } = useSelector(
        OutputReportSelectors.getList,
    );
    const isLogged = useIsLoggedIn();
    const dispatch = useDispatch<AppDispatch>();
    useScrollToTop();
    const locale = useSelector(LanguageSlice.state.getLocale);

    useEffect(() => {
        if (!isLogged) {
            return;
        }

        dispatch(OutputReportActions.getReports());
    }, [isLogged]);

    const [dialogState, setDialogState] = useState<{
        report?: IReport;
        visible: boolean;
    }>({
        report: undefined,
        visible: false,
    });

    const langIsSupported = useMemo(
        () =>
            locale &&
            dialogState.report?.languages &&
            languageIsSupported(locale, dialogState.report.languages),
        [locale, dialogState.report],
    );
    const navigate = useNavigate();
    useEffect(() => {
        if (dialogState.report?.id && !dialogState.visible && langIsSupported) {
            goToOutput(dialogState.report.id);
        }
    }, [dialogState.report?.id, navigate, langIsSupported]);

    const portal = usePortal();
    useEffect(() => {
        if (dialogState.visible) {
            portal.setContent(
                <LanguageDialog
                    options={dialogState.report?.languages}
                    isVisible={dialogState.visible}
                    onClose={closeDialog}
                    onConfirm={confirmDialog}
                />,
            );
        } else {
            portal.setContent(null);
        }
    }, [dialogState.visible]);

    return <ProjectsPageLayout>{render(reports, loading)}</ProjectsPageLayout>;

    function render(reports: IReports[], loading: boolean) {
        if (loading) {
            return <ProjectsSection name="" isLoading={loading} />;
        }

        if (reports?.length === 0) {
            return (
                <NoData>
                    <Translate textOnly>fe.results.list.empty</Translate>
                </NoData>
            );
        }

        return reports.map((item, i) => (
            <ProjectsSection key={i} name={item.name} isLoading={false}>
                {item.reports.map(
                    (report, j) =>
                        report?.id && (
                            <OutputReportCard
                                key={j}
                                report={report}
                                onClick={showDialog}
                            />
                        ),
                )}
            </ProjectsSection>
        ));
    }

    function showDialog(report: IReport) {
        if (locale && languageIsSupported(locale, report.languages)) {
            goToOutput(report.id);
            return;
        }

        setDialogState({ report, visible: true });
    }

    function closeDialog() {
        setDialogState({
            report: undefined,
            visible: false,
        });
    }

    function confirmDialog(locale: string) {
        dispatch(LanguageSlice.actions.changeLocale(locale));
        setDialogState((prev) => ({
            ...prev,
            visible: false,
        }));
    }

    function goToOutput(id: number) {
        navigate(
            generatePath(OUTPUT_REPORT_DETAIL_PATH, {
                id: id.toString(),
            }),
        );
    }

    function languageIsSupported(language: string, languages: string[]) {
        return languages?.includes(language.toLowerCase());
    }
}
